import { DogDto, DogProposedChangesDto, IDog, IDogProposedChanges } from "@eagerdog/interfaces";
import { apiService } from "src/services";

import { mergeProposedClubRegistrations } from "./fields";

export { mergeProposedClubRegistrations };

export const isProposedChangesEdit = (dog: IDog, userId: string, isAdmin?: boolean, eventId?: string) => {
  return !!(isAdmin && eventId && dog.owner_id !== userId);
};

export function getChangedFields(original: any, updated: any) {
  const changes: any = {};

  for (const key of Object.keys(updated)) {
    if ([
      "registration_numbers",
      "suffix_title",
      "prefix_title",
      "call_name",
      "registered_name",
      "variety",
      "breed",
    ].includes(key)) continue;

    const oldValue = original?.[key];
    const newValue = updated[key];

    if (Array.isArray(newValue)) {
      if (key === "coowners") {
        const oldArray = oldValue || [];
        const newArray = newValue || [];

        if (JSON.stringify(oldArray) !== JSON.stringify(newArray)) {
          changes[key] = newArray.map((item: any, index: number) => ({
            old: oldArray[index] || undefined,
            new: item,
          }));
        }
        continue;
      }

      const idKey = "sanctioning_club";
      const mapOld = new Map<string, any>();
      (oldValue || []).forEach((item: any) => {
        if (item?.[idKey]) mapOld.set(item[idKey], item);
      });

      const arrChanges: { old?: any; new?: any }[] = [];
      (newValue || []).forEach((item: any) => {
        const id = item[idKey];
        const oldItem = mapOld.get(id);
        if (JSON.stringify(oldItem) !== JSON.stringify(item)) {
          arrChanges.push({
            old: oldItem || undefined,
            new: item,
          });
        }
        mapOld.delete(id);
      });

      if (arrChanges.length > 0) {
        changes[key] = arrChanges;
      }
    } else if (
      newValue !== null &&
      typeof newValue === "object" &&
      !(newValue instanceof Date)
    ) {
      const nestedChanges = getChangedFields(oldValue || {}, newValue);
      if (Object.keys(nestedChanges).length > 0) {
        changes[key] = nestedChanges;
      }
    } else if (newValue instanceof Date) {
      if (newValue?.toISOString() !== oldValue) {
        changes[key] = { old: oldValue, new: newValue };
      }
    } else if (newValue !== oldValue) {
      changes[key] = { old: oldValue, new: newValue };
    }
  }

  return changes;
}

const comparisonBase = (dog: IDog, proposedChanges?: IDogProposedChanges) => {
  if (!proposedChanges) return dog;
  return {
    ...dog,
    club_registrations: mergeProposedClubRegistrations(dog, proposedChanges),
    dob: proposedChanges.dob?.new || dog.dob,
    sex: proposedChanges.sex?.new || dog.sex,
    weight: proposedChanges.weight?.new || dog.weight,
    height_at_withers: proposedChanges.height_at_withers?.new || dog.height_at_withers,
    final_response: proposedChanges.final_response?.new || dog.final_response,
    country_of_birth: proposedChanges.country_of_birth?.new || dog.country_of_birth,
    breeder: proposedChanges.breeder?.new || dog.breeder,
    sire: proposedChanges.sire?.new || dog.sire,
    dam: proposedChanges.dam?.new || dog.dam,
  };
};

/**
 * Same save branch DogForm uses: proposed changes when an admin edits someone
 * else's dog for an event, otherwise a normal dog update.
 * `payload` is the flat dog shape, not the proposed-changes diff.
 */
export const persistDogUpdate = async (args: {
  dog: IDog;
  userId: string;
  isAdmin?: boolean;
  eventId?: string;
  proposedChanges?: IDogProposedChanges;
  payload: DogDto | DogProposedChangesDto | Record<string, unknown>;
}): Promise<IDog> => {
  const proposedEdit = isProposedChangesEdit(args.dog, args.userId, args.isAdmin, args.eventId);
  let body: any = args.payload;

  if (proposedEdit) {
    body = getChangedFields(comparisonBase(args.dog, args.proposedChanges), args.payload);
    body.event_id = args.eventId;
  }

  if (args.eventId) {
    body.registration_numbers = [];
  }

  if (proposedEdit) {
    return apiService.makeDogProposedChanges(args.dog._id, body as DogProposedChangesDto);
  }

  return apiService.updateDog(args.dog._id, body as DogDto);
};
